import * as React from 'react';
import Grid from '@mui/material/Grid';
import {Typography, Box, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField, Button, Card, CardContent, TableSortLabel, Tooltip } from '@mui/material';
import { collection,collectionGroup, doc, getDoc, getDocs, orderBy, query, where, setDoc, Timestamp, deleteField } from 'firebase/firestore';
import config from "../utils/config";
import DashboardTile from "./DashboardTile";
import Config from '../utils/config';
import { useAuthController } from '@camberi/firecms';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, LineElement, PointElement, LinearScale, Title } from 'chart.js';
import 'chart.js/auto'; // ADD THIS

const SHOWMATCHESINGRAPH = false; // Toggle to show/hide matches in the analytics graph
const LAST_DAYS = 13;


let _setOpen:any;
let _setModalData:any;
let authController:any;

let defaultGraphOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
        position: 'top' as const,
        },     
    },
};  

let defaultGraph = {
      datasets: [{
        data: [0],
      }],
      labels: ['no date']
  }

export function Analytics(props: {setOpen:any, setModalData:any}) {
    _setOpen = props.setOpen;
    _setModalData = props.setModalData;
    authController = useAuthController();
    

    const [setFields, setSetFields] = React.useState([]); // the dev collections
    const [showExtMetrics, setShowExtMetrics] = React.useState(false);
    const [extendedDevMetrics, setExtendedDevMetrics ] = React.useState([]);
    const datePickerRef = React.useRef<HTMLDivElement>(null);
    
    // Date range tool state
    const [startDate, setStartDate] = React.useState('');
    const [endDate, setEndDate] = React.useState('');
    const [adSpent, setAdSpent] = React.useState<number | ''>('');
    const [userStats, setUserStats] = React.useState<any>(null);
    const [loadingUserStats, setLoadingUserStats] = React.useState(false);
    const [loadingMessage, setLoadingMessage] = React.useState('');
    const [sortConfig, setSortConfig] = React.useState<{key: string, direction: 'asc' | 'desc'}>({key: 'total', direction: 'desc'});
    


    // Helper functions for sorting and data processing
    const handleSort = (key: string) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const copyToClipboard = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
            // You could add a toast notification here if desired
        } catch (err) {
            console.error('Failed to copy text: ', err);
        }
    };

    const getSortedSalesData = () => {
        if (!userStats?.salesStats) return [];
        
        const salesArray = Object.entries(userStats.salesStats);
        return salesArray.sort((a, b) => {
            const [itemA, statsA] = a;
            const [itemB, statsB] = b;
            
            let aValue, bValue;
            switch (sortConfig.key) {
                case 'item':
                    aValue = itemA;
                    bValue = itemB;
                    break;
                case 'total':
                    aValue = (statsA as any).total;
                    bValue = (statsB as any).total;
                    break;
                case 'average':
                    aValue = (statsA as any).average;
                    bValue = (statsB as any).average;
                    break;
                case 'median':
                    aValue = (statsA as any).median;
                    bValue = (statsB as any).median;
                    break;
                case 'min':
                    aValue = (statsA as any).min;
                    bValue = (statsB as any).min;
                    break;
                case 'max':
                    aValue = (statsA as any).max;
                    bValue = (statsB as any).max;
                    break;
                case 'users':
                    aValue = (statsA as any).users;
                    bValue = (statsB as any).users;
                    break;
                default:
                    return 0;
            }
            
            if (typeof aValue === 'string') {
                return sortConfig.direction === 'asc' 
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
            }
            
            return sortConfig.direction === 'asc' 
                ? aValue - bValue 
                : bValue - aValue;
        });
    };

    // Calculate statistics for an array of numbers
    const calculateStats = (values: number[]) => {
        if (values.length === 0) return { average: 0, median: 0, min: 0, max: 0 };
        
        const sorted = [...values].sort((a, b) => a - b);
        const sum = values.reduce((acc, val) => acc + val, 0);
        const average = Math.round((sum / values.length) * 100) / 100;
        const median = sorted.length % 2 === 0 
            ? Math.round(((sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2) * 100) / 100
            : Math.round(sorted[Math.floor(sorted.length / 2)] * 100) / 100;
        const min = Math.min(...values);
        const max = Math.max(...values);
        
        return { average, median, min, max };
    };

    // Calculate LTV (Lifetime Value) for USD sales
    const calculateLTV = async (userDocs: any[], startDate: Date, endDate: Date, setLoadingMessage?: (message: string) => void) => {
        const ltvData: { [key: string]: number } = {
            D1: 0,
            D7: 0,
            D14: 0,
            D30: 0,
            D60: 0,
            D90: 0
        };
        
        let totalProcessedAmount = 0;
        let totalSalesProcessed = 0;
        let totalUsersWithSales = 0;
        
        let processedUsers = 0;
        for (const userDoc of userDocs) {
            const userId = userDoc.id;
            let userTotalAmount = 0;
            let userSalesCount = 0;
            
            try {
                processedUsers++;
                
                // Update loading message every 10 users
                if (processedUsers % 10 === 0 && setLoadingMessage) {
                    setLoadingMessage(`Calculating LTV... Processing user ${processedUsers}/${userDocs.length}`);
                }
                
                // First, check the sales subcollection for individual USD transactions
                const salesCollection = collection(Config.getLiveDB(), "users", userId, "sales");
                const salesSnapshot = await getDocs(salesCollection);
                
                for (const saleDoc of salesSnapshot.docs) {
                    const saleData = saleDoc.data();
                    
                    // Get the timestamp
                    const timestamp = saleData.timestamp || saleData.time || saleData.date;
                    
                    if (timestamp) {
                        // Convert Firestore timestamp to Date
                        const saleDate = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
                        
                        // Only process sales that occurred after the start date
                        if (saleDate >= startDate) {
                            // Get the SKU and amount from items array
                            if (saleData.items && Array.isArray(saleData.items) && saleData.items.length > 0) {
                                const sku = saleData.items[0].sku;
                                const amount = parseFloat(saleData.items[0].amount) || 0;
                                
                                // Check if this is a USD sale - look for USD in currency or SKU
                                if (saleData.currency === 'USD' || sku.includes('USD') || sku === 'USD') {
                                    // For USD sales, the amount might be in a different field
                                    const usdAmount = saleData.amount || saleData.value || saleData.price || amount;
                                    
                                    if (usdAmount > 0) {
                                        // Calculate days since start date (user registration date)
                                        const daysSinceStart = Math.floor((saleDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
                                        
                                        userTotalAmount += usdAmount;
                                        userSalesCount++;
                                        totalProcessedAmount += usdAmount;
                                        totalSalesProcessed++;
                                        
                                        // Add amount to appropriate day buckets based on when the sale occurred
                                        if (daysSinceStart >= 0 && daysSinceStart <= 1) {
                                            ltvData.D1 += usdAmount;
                                        }
                                        if (daysSinceStart > 1 && daysSinceStart <= 7) {
                                            ltvData.D7 += usdAmount;
                                        }
                                        if (daysSinceStart > 7 && daysSinceStart <= 14) {
                                            ltvData.D14 += usdAmount;
                                        }
                                        if (daysSinceStart > 14 && daysSinceStart <= 30) {
                                            ltvData.D30 += usdAmount;
                                        }
                                        if (daysSinceStart > 30 && daysSinceStart <= 60) {
                                            ltvData.D60 += usdAmount;
                                        }
                                        if (daysSinceStart > 60 && daysSinceStart <= 90) {
                                            ltvData.D90 += usdAmount;
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                
                if (userTotalAmount > 0) {
                    totalUsersWithSales++;
                }
                
            } catch (error) {
                console.warn(`Error processing LTV for user ${userId}:`, error);
            }
        }
        
        return ltvData;
    };

    // Main function to analyze users in date range
    const analyzeUsersInDateRange = async (startDateStr: string, endDateStr: string) => {
        const registrationStartDate = new Date(startDateStr);
        registrationStartDate.setHours(0, 0, 0, 0);
        const registrationEndDate = new Date(endDateStr);
        registrationEndDate.setHours(23, 59, 59, 999);
        
        // Convert to Firestore timestamps
        const startTimestamp = Timestamp.fromDate(registrationStartDate);
        const endTimestamp = Timestamp.fromDate(registrationEndDate);
        
        console.log(`Querying users created between ${registrationStartDate.toISOString()} and ${registrationEndDate.toISOString()}`);
        setLoadingMessage('Querying users...');
        
        // Query users collection where creationTime is within range
        const usersQuery = query(
            collection(Config.getLiveDB(), "users"),
            where("creationTime", ">=", startTimestamp),
            where("creationTime", "<=", endTimestamp),
            orderBy("creationTime", "asc")
        );
        
        const usersSnapshot = await getDocs(usersQuery);
        console.log(`Found ${usersSnapshot.size} users in date range`);
        setLoadingMessage(`Found ${usersSnapshot.size} users in date range`);
        
        if (usersSnapshot.empty) {
            return {
                totalUsers: 0,
                matchesStats: { average: 0, median: 0, min: 0, max: 0, winRate: 0 },
                salesStats: {}
            };
        }
        
        // Fields to exclude from sales analysis
        const excludedFields = ['freeItem', 'emote0', 'USD', 'defaultSleeve', 'finisher0', 'mainBanner01', 'mainBanner0', 'emptyFrame', 'mainBanner02'];
        
        // Initialize accumulators
        const matchesPlayed: number[] = [];
        const matchesWon: number[] = [];
        const winRates: number[] = [];
        const matchesPlayedUserIds: string[] = [];
        const winRatesUserIds: string[] = [];
        const salesData: { [item: string]: { values: number[], userIds: string[] } } = {};
        let processedUsers = 0;
        
        // Process each user
        for (const userDoc of usersSnapshot.docs) {
            const userId = userDoc.id;
            
            try {
                // Get gameplay metrics
                const gameplayDoc = await getDoc(doc(Config.getLiveDB(), "users", userId, "metrics", "gameplay"));
                if (gameplayDoc.exists()) {
                    const gameplayData = gameplayDoc.data();
                    const played = gameplayData.matchesPlayed || 0;
                    const won = gameplayData.matchesWon || 0;
                    
                    matchesPlayed.push(played);
                    matchesWon.push(won);
                    matchesPlayedUserIds.push(userId);
                    
                    // Calculate individual win rate
                    const winRate = played > 0 ? (won / played) * 100 : 0;
                    winRates.push(winRate);
                    winRatesUserIds.push(userId);
                }
                
                // Get sales metrics
                const salesDoc = await getDoc(doc(Config.getLiveDB(), "users", userId, "metrics", "sales"));
                if (salesDoc.exists()) {
                    const salesDataUser = salesDoc.data();
                    
                    Object.entries(salesDataUser || {}).forEach(([key, value]) => {
                        // Skip excluded fields
                        if (excludedFields.includes(key)) {
                            return;
                        }
                        
                        const numericValue = typeof value === 'number' ? value : 0;
                        if (numericValue > 0) {
                            if (!salesData[key]) {
                                salesData[key] = { values: [], userIds: [] };
                            }
                            salesData[key].values.push(numericValue);
                            salesData[key].userIds.push(userId);
                        }
                    });
                }
                
                processedUsers++;
                
                // Progress indicator
                if (processedUsers % 50 === 0) {
                    console.log(`Processed ${processedUsers}/${usersSnapshot.size} users...`);
                    setLoadingMessage(`Processed ${processedUsers}/${usersSnapshot.size} users...`);
                }
                
            } catch (error) {
                console.warn(`Error processing user ${userId}:`, error);
            }
        }
        
        console.log(`Processed ${processedUsers} users`);
        setLoadingMessage(`Processed ${processedUsers} users`);
        
        // Calculate matches statistics
        const matchesStats = calculateStats(matchesPlayed);
        const totalMatchesWon = matchesWon.reduce((sum, won) => sum + won, 0);
        const totalMatchesPlayed = matchesPlayed.reduce((sum, played) => sum + played, 0);
        const overallWinRate = totalMatchesPlayed > 0 ? Math.round((totalMatchesWon / totalMatchesPlayed) * 100) : 0;
        
        // Find user IDs for min/max matches played
        if (matchesPlayed.length > 0) {
            const sortedMatchesPlayed = [...matchesPlayed].sort((a, b) => a - b);
            const minMatchesIndex = matchesPlayed.findIndex(x => x === sortedMatchesPlayed[0]);
            const maxMatchesIndex = matchesPlayed.findIndex(x => x === sortedMatchesPlayed[sortedMatchesPlayed.length - 1]);
            (matchesStats as any).minUserId = matchesPlayedUserIds[minMatchesIndex];
            (matchesStats as any).maxUserId = matchesPlayedUserIds[maxMatchesIndex];
        }
        
        // Calculate win rate statistics
        const winRateStats = calculateStats(winRates);
        if (winRates.length > 0) {
            const sortedWinRates = [...winRates].sort((a, b) => a - b);
            const minWinRateIndex = winRates.findIndex(x => x === sortedWinRates[0]);
            const maxWinRateIndex = winRates.findIndex(x => x === sortedWinRates[sortedWinRates.length - 1]);
            (winRateStats as any).minUserId = winRatesUserIds[minWinRateIndex];
            (winRateStats as any).maxUserId = winRatesUserIds[maxWinRateIndex];
        }
        
        (matchesStats as any).winRate = overallWinRate;
        (matchesStats as any).winRateStats = winRateStats;
        
        // Calculate sales statistics
        const salesStats: { [item: string]: any } = {};
        Object.entries(salesData).forEach(([item, data]) => {
            const stats = calculateStats(data.values);
            const sortedValues = [...data.values].sort((a, b) => a - b);
            const minIndex = data.values.indexOf(sortedValues[0]);
            const maxIndex = data.values.indexOf(sortedValues[sortedValues.length - 1]);
            
            salesStats[item] = {
                ...stats,
                total: Math.round((data.values.reduce((sum, val) => sum + val, 0)) * 100) / 100,
                users: data.values.length,
                minUserId: data.userIds[minIndex],
                maxUserId: data.userIds[maxIndex]
            };
        });
        
        // Calculate LTV (Lifetime Value) for USD sales
        setLoadingMessage('Calculating LTV...');
        const ltvStats = await calculateLTV(usersSnapshot.docs, registrationStartDate, registrationEndDate, setLoadingMessage);
        
        return {
            totalUsers: usersSnapshot.size,
            matchesStats,
            salesStats,
            ltvStats
        };
    };

    React.useEffect(()=>{
        getSetsCollection("set", "sets", setSetFields);
        
        // Auto-load last 14 days (exclude today)
        const today = new Date();
        const end = new Date(today);
        end.setDate(today.getDate() - 1); // yesterday
        end.setHours(23,59,59,999);
        const start = new Date(end);
        start.setDate(end.getDate() - LAST_DAYS);
        start.setHours(0,0,0,0);
        getExtendedDevMetrics(setExtendedDevMetrics, start, end, setShowExtMetrics);
        
    }, []);

    const renderDateRangeTool = () => {
        const handleAnalyze = async () => {
            if (!startDate || !endDate) {
                alert('Please select both start and end dates');
                return;
            }
            
            setLoadingUserStats(true);
            try {
                const stats = await analyzeUsersInDateRange(startDate, endDate);
                setUserStats(stats);
            } catch (error) {
                console.error('Error analyzing users:', error);
                alert('Error analyzing users. Please try again.');
            } finally {
                setLoadingUserStats(false);
            }
        };

        return (
            <Card sx={{ width: "100%", margin: "25px", padding: "20px" }}>
                <CardContent>
                    <Typography variant="h6" sx={{ marginBottom: 2 }}>
                        User Registration Analysis Tool
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center", marginBottom: 2 }}>
                        <TextField
                            label="Start Date"
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ minWidth: 200 }}
                        />
                        <TextField
                            label="End Date"
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ minWidth: 200 }}
                        />
                        <TextField
                            label="Ad Spent"
                            type="number"
                            value={adSpent}
                            onChange={(e) => setAdSpent(e.target.value === '' ? '' : parseFloat(e.target.value))}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ min: 0, step: 0.01 }}
                            sx={{ minWidth: 150 }}
                            placeholder="0.00"
                        />
                        <Button
                            variant="contained"
                            onClick={handleAnalyze}
                            disabled={loadingUserStats || !startDate || !endDate}
                            sx={{ minWidth: 120 }}
                        >
                            {loadingUserStats ? <CircularProgress size={20} /> : 'Analyze'}
                        </Button>
                    </Box>
                    {loadingUserStats && loadingMessage && (
                        <Typography variant="body2" sx={{ marginTop: 1, color: 'text.secondary' }}>
                            {loadingMessage}
                        </Typography>
                    )}
                </CardContent>
            </Card>
        );
    };

    const renderUserStats = () => {
        if (!userStats) return null;

        const { totalUsers, matchesStats, salesStats, ltvStats } = userStats;

        return (
            <Card sx={{ width: "100%", margin: "25px", padding: "20px" }}>
                <CardContent>
                    <Typography variant="h6" sx={{ marginBottom: 2 }}>
                        Analysis Results
                    </Typography>
                    
                    <Typography variant="body1" sx={{ marginBottom: 2 }}>
                        <strong>{totalUsers}</strong> users registered during the period.
                    </Typography>
                    
                    <Typography variant="body1" sx={{ marginBottom: 2 }}>
                        Matches Played: <strong>{matchesStats.average}</strong> average, <strong>{matchesStats.median}</strong> median, 
                        <Tooltip title={`User ID: ${(matchesStats as any).minUserId}`} arrow>
                            <span 
                                style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                onClick={() => copyToClipboard((matchesStats as any).minUserId)}
                            >
                                <strong> {matchesStats.min}</strong>
                            </span>
                        </Tooltip> minimum, 
                        <Tooltip title={`User ID: ${(matchesStats as any).maxUserId}`} arrow>
                            <span 
                                style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                onClick={() => copyToClipboard((matchesStats as any).maxUserId)}
                            >
                                <strong> {matchesStats.max}</strong>
                            </span>
                        </Tooltip> maximum
                    </Typography>
                    
        
                    
                    <Typography variant="body1" sx={{ marginBottom: 2 }}>
                        Individual Win Rates: <strong>{(matchesStats as any).winRateStats?.average || 0}%</strong> average, 
                        <strong> {(matchesStats as any).winRateStats?.median || 0}%</strong> median, 
                        <Tooltip title={`User ID: ${(matchesStats as any).winRateStats?.minUserId}`} arrow>
                            <span 
                                style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                onClick={() => copyToClipboard((matchesStats as any).winRateStats?.minUserId)}
                            >
                                <strong> {(matchesStats as any).winRateStats?.min || 0}%</strong>
                            </span>
                        </Tooltip> minimum, 
                        <Tooltip title={`User ID: ${(matchesStats as any).winRateStats?.maxUserId}`} arrow>
                            <span 
                                style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                onClick={() => copyToClipboard((matchesStats as any).winRateStats?.maxUserId)}
                            >
                                <strong> {(matchesStats as any).winRateStats?.max || 0}%</strong>
                            </span>
                        </Tooltip> maximum
                    </Typography>


                    {ltvStats && (
                        <Box sx={{ marginTop: 3 }}>
                            <Typography variant="h6" sx={{ marginBottom: 2 }}>
                                LTV (Lifetime Value) - USD Sales
                            </Typography>
                            <TableContainer component={Paper} sx={{ maxWidth: 600 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Day</TableCell>
                                            <TableCell align="right">Total USD</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {Object.entries(ltvStats).map(([day, amount]: [string, any]) => (
                                            <TableRow key={day}>
                                                <TableCell component="th" scope="row">{day}</TableCell>
                                                <TableCell align="right">
                                                    {amount > 0 ? `$${amount.toFixed(2)}` : 'n/a'}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {(() => {
                                            const totalLTV = Object.values(ltvStats).reduce((sum: number, amount: any) => sum + (amount || 0), 0);
                                            const isProfitable = adSpent !== '' && typeof adSpent === 'number' && totalLTV >= adSpent;
                                            const hasAdSpent = adSpent !== '' && typeof adSpent === 'number';
                                            const margin = hasAdSpent ? totalLTV - adSpent : null;
                                            
                                            return (
                                                <>
                                                    <TableRow>
                                                        <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Total</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 'bold', color: hasAdSpent ? (isProfitable ? 'success.main' : 'error.main') : 'inherit' }}>
                                                            ${totalLTV.toFixed(2)}
                                                        </TableCell>
                                                    </TableRow>
                                                    {margin !== null && (
                                                        <TableRow>
                                                            <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', color: margin >= 0 ? 'success.main' : 'error.main' }}>
                                                                {margin >= 0 ? 'Profit' : 'Loss'}
                                                            </TableCell>
                                                            <TableCell align="right" sx={{ fontWeight: 'bold', color: margin >= 0 ? 'success.main' : 'error.main' }}>
                                                                ${Math.abs(margin).toFixed(2)}
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </>
                                            );
                                        })()}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}

                    {Object.keys(salesStats).length > 0 && (
                        <Box sx={{ marginTop: 3 }}>
                            <Typography variant="h6" sx={{ marginBottom: 2 }}>
                                Sales Statistics
                            </Typography>
                            <TableContainer component={Paper}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>
                                                <TableSortLabel
                                                    active={sortConfig.key === 'item'}
                                                    direction={sortConfig.key === 'item' ? sortConfig.direction : 'asc'}
                                                    onClick={() => handleSort('item')}
                                                >
                                                    Item
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell align="right">
                                                <TableSortLabel
                                                    active={sortConfig.key === 'total'}
                                                    direction={sortConfig.key === 'total' ? sortConfig.direction : 'asc'}
                                                    onClick={() => handleSort('total')}
                                                >
                                                    Total
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell align="right">
                                                <TableSortLabel
                                                    active={sortConfig.key === 'average'}
                                                    direction={sortConfig.key === 'average' ? sortConfig.direction : 'asc'}
                                                    onClick={() => handleSort('average')}
                                                >
                                                    Average
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell align="right">
                                                <TableSortLabel
                                                    active={sortConfig.key === 'median'}
                                                    direction={sortConfig.key === 'median' ? sortConfig.direction : 'asc'}
                                                    onClick={() => handleSort('median')}
                                                >
                                                    Median
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell align="right">
                                                <TableSortLabel
                                                    active={sortConfig.key === 'min'}
                                                    direction={sortConfig.key === 'min' ? sortConfig.direction : 'asc'}
                                                    onClick={() => handleSort('min')}
                                                >
                                                    Min
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell align="right">
                                                <TableSortLabel
                                                    active={sortConfig.key === 'max'}
                                                    direction={sortConfig.key === 'max' ? sortConfig.direction : 'asc'}
                                                    onClick={() => handleSort('max')}
                                                >
                                                    Max
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell align="right">
                                                <TableSortLabel
                                                    active={sortConfig.key === 'users'}
                                                    direction={sortConfig.key === 'users' ? sortConfig.direction : 'asc'}
                                                    onClick={() => handleSort('users')}
                                                >
                                                    Users
                                                </TableSortLabel>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {getSortedSalesData().map(([item, stats]: [string, any]) => (
                                            <TableRow key={item}>
                                                <TableCell component="th" scope="row">{item}</TableCell>
                                                <TableCell align="right">{stats.total}</TableCell>
                                                <TableCell align="right">{stats.average}</TableCell>
                                                <TableCell align="right">{stats.median}</TableCell>
                                                <TableCell align="right">
                                                    <Tooltip title={`User ID: ${stats.minUserId}`} arrow>
                                                        <span 
                                                            style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                                            onClick={() => copyToClipboard(stats.minUserId)}
                                                        >
                                                            {stats.min}
                                                        </span>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Tooltip title={`User ID: ${stats.maxUserId}`} arrow>
                                                        <span 
                                                            style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                                            onClick={() => copyToClipboard(stats.maxUserId)}
                                                        >
                                                            {stats.max}
                                                        </span>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell align="right">{stats.users}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}

                    
                </CardContent>
            </Card>
        );
    };
    
    return (
        <Grid container sx={{ m:4, width:"90%"}}>
            { !showExtMetrics && (
                <Box sx={{width:"100%", display:"flex", justifyContent:"center", margin:"25px"}}>
                    <CircularProgress />
                </Box>
            )}
            { renderDateRangeTool() }
            { renderUserStats() }
            { renderPlaytestingStats (extendedDevMetrics, showExtMetrics) }
            { renderRetentionTable (extendedDevMetrics, showExtMetrics) }
            {/* { renderSegment("sets", setFields) } */}
           
        </Grid>
    );
}





const renderPlaytestingStats = (extendedDevMetrics:any, showExtMetrics:boolean) => {
    
    return(<Box sx={{ width:"100%" }}>
        
        
        <Grid container spacing={2}>
            
            {/* informational text removed (registered users no longer fetched) */}
            { showExtMetrics && extendedDevMetrics.averageTurns !== undefined && (
                <Typography sx={{ width:"100%", margin:"0 25px" }}>
                    Average turns per battle: {extendedDevMetrics.averageTurns} — Average time per battle: {extendedDevMetrics.averageMinutes} minutes
                </Typography>
            )}
            { showExtMetrics && (
                <Box sx={{ height:"500px", width:"100%", margin:"0 25px"}}> 
                 { <Line 
                    key={(extendedDevMetrics.graphData && extendedDevMetrics.graphData.labels) ? extendedDevMetrics.graphData.labels.join('|') : 'empty'}
                    options={(extendedDevMetrics.graphData == undefined) ? defaultGraphOptions : extendedDevMetrics.graphOptions} 
                    data={ (extendedDevMetrics.graphData == undefined) ? defaultGraph : extendedDevMetrics.graphData}  /> }
                </Box>
            )}
            
            
      </Grid>

        </Box>
        
    )
}

const renderRetentionTable = (extendedDevMetrics:any, showExtMetrics:boolean) => {
    if (!showExtMetrics || !extendedDevMetrics.retentionData) {
        return null;
    }

    const { retentionData } = extendedDevMetrics;
    const { retentionPercentages, baseUsers } = retentionData;

    return (
        <Box sx={{ width:"100%", margin:"25px" }}>
            <Typography variant="h6" sx={{ marginBottom: 2 }}>
                {/* User Retention ({baseUsers} base users) */}
                User Retention
            </Typography>
            <TableContainer component={Paper} sx={{ maxWidth: 300 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Day</TableCell>
                            <TableCell align="right">Retention</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell component="th" scope="row">D1</TableCell>
                            <TableCell align="right">{retentionPercentages.D1 || 0}%</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell component="th" scope="row">D7</TableCell>
                            <TableCell align="right">{retentionPercentages.D7 || 0}%</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell component="th" scope="row">D14</TableCell>
                            <TableCell align="right">{retentionPercentages.D14 || 0}%</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell component="th" scope="row">D21</TableCell>
                            <TableCell align="right">{retentionPercentages.D21 || 0}%</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell component="th" scope="row">D30</TableCell>
                            <TableCell align="right">{retentionPercentages.D30 || 0}%</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}





const getSetsCollection = async (type:string, collectionname:string, setFields:any) => {
    let promise = new Promise(async (resolve, reject) => { 
      // get public documents      
      let q = query(collectionGroup(config.getDevDB(), "public"), where("type", "==", type) );    
      let querySnapshot = await getDocs(q);
      let currentCollection :any []= [];
      await querySnapshot.forEach((doc) => {
        currentCollection.push(doc.data().docid);
      });
    // get data of public documents 
      let collectionArray : any = [];
      let i = 0;
      currentCollection.forEach( async ( publicID) => {

          let ref = await getDoc(doc(config.getDevDB(), collectionname, publicID));
          
          let data:any = ref.data();
          
          if (data == undefined) return;
          data.docid = ref.id;
         // if (data.abbreviation != undefined) { // this mean we're getting the reference set and not the subcollection
            collectionArray.push(data);
            i++;
            if (i >= currentCollection.length) {
                resolve("success");
            }
            setFields(collectionArray);
        //  }
          
      })
    });
  
    let result = await promise;
    return result;  
};


// core dev metrics removed

const calculateRetention = async (fromDate: any, toDate: any) => {
    const startDate = new Date(fromDate);
    startDate.setHours(0,0,0,0);
    const endDate = new Date(toDate);
    endDate.setHours(23,59,59,999);

    // Get all login documents in the date range
    const loginTimesQuery = query(
        collectionGroup(Config.getLiveDB(), "logins"),
        where("loginTime", ">=", startDate),
        where("loginTime", "<=", endDate)
    );
    const loginTimesSnapshot = await getDocs(loginTimesQuery);

    // Filter out documents that have fromUnity field set to false
    const filteredDocs = loginTimesSnapshot.docs.filter((doc: any) => {
        const data = doc.data();
        return data.fromUnity !== false;
    });

    let firstDayUsers: any[] = [];
    let remainingUsers: any[] = [];

    // Process each login document
    filteredDocs.forEach((doc: any) => {
        const userID = doc.ref.parent.parent.id;
        const loginTime = doc.data().loginTime;
        const loginDate = new Date(loginTime.seconds * 1000);
        const dateDocId = formatDateId(loginDate);
        
        // Check if this is the first day (cohort day)
        if (loginDate.getTime() >= startDate.getTime() && loginDate.getTime() < startDate.getTime() + 24 * 60 * 60 * 1000) {
            firstDayUsers.push({ uid: userID, date: dateDocId });
        } else {
            remainingUsers.push({ uid: userID, date: dateDocId });
        }
    });

    // Group remaining users by date
    const remainingUsersSortedByDate = remainingUsers.reduce((result: any, item: any) => {
        const { date } = item;
        let groupList = result[date];
        if (!groupList) {
            groupList = result[date] = [];
        }
        groupList.push(item);
        return result;
    }, {});

    // Calculate retention for each day
    const retention: any[] = [];
    for (const [key, value] of Object.entries(remainingUsersSortedByDate)) {
        const obj: any = value;
        const usersFound: any[] = [];
        obj.forEach((item: any) => {
            const result = firstDayUsers.find((obj: any) => obj.uid === item.uid);
            if (result) {
                usersFound.push(result);
            }
        });
        retention.push({ date: key, returningUsers: usersFound });
    }

    // Calculate retention percentages for D1, D7, D14, D21, D30
    const retentionPercentages: any = {};
    const baseUsers = firstDayUsers.length;
    
    if (baseUsers > 0) {
        retentionPercentages.D1 = Math.round((retention[0]?.returningUsers.length || 0) * 100 / baseUsers);
        retentionPercentages.D7 = Math.round((retention[6]?.returningUsers.length || 0) * 100 / baseUsers);
        retentionPercentages.D14 = Math.round((retention[13]?.returningUsers.length || 0) * 100 / baseUsers);
        retentionPercentages.D21 = Math.round((retention[20]?.returningUsers.length || 0) * 100 / baseUsers);
        retentionPercentages.D30 = Math.round((retention[29]?.returningUsers.length || 0) * 100 / baseUsers);
    }

    return {
        baseUsers,
        retentionPercentages,
        fullRetention: retention
    };
};

const formatDateId = (date: Date) => {
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = String(date.getFullYear());
    return `${dd}.${mm}.${yyyy}`;
};

// Helper to convert array format to object format for backward compatibility
const arrayToObject = (arr: Array<{date: string, value: any}>): Record<string, any> => {
    const obj: Record<string, any> = {};
    arr.forEach(item => {
        obj[item.date] = item.value;
    });
    return obj;
};

// Helper to convert object format to array format to avoid index limits
const objectToArray = (obj: Record<string, any>): Array<{date: string, value: any}> => {
    return Object.entries(obj || {}).map(([date, value]) => ({ date, value }));
};

const getCachedAnalyticsData = async (startDate: Date, endDate: Date, labels: string[]) => {
    // Get cached data from Firestore
    const cachedDoc = await getDoc(doc(Config.getLiveDB(), "settings", "cachedAnalytics"));
    const cachedData = cachedDoc.exists() ? cachedDoc.data() : {};
    
    const labelsSet = new Set(labels);
    
    // Convert cached data from array format (new) or object format (old) to object format for processing
    const getCachedAsObject = (cachedArray: any, cachedObject: any): Record<string, any> => {
        if (cachedArray && Array.isArray(cachedArray)) {
            return arrayToObject(cachedArray);
        }
        return cachedObject || {};
    };
    
    const pruneDataset = (dataset?: Record<string, any>) => {
        const sanitized: Record<string, any> = {};
        const staleKeys: string[] = [];
        Object.entries(dataset || {}).forEach(([key, value]) => {
            if (labelsSet.has(key)) {
                sanitized[key] = value;
                return;
            }
            staleKeys.push(key);
        });
        return { sanitized, staleKeys };
    };

    const cachedLoginsObj = getCachedAsObject(cachedData.dailyLoginsArray, cachedData.dailyLogins);
    const cachedNewUsersObj = getCachedAsObject(cachedData.dailyNewUsersArray, cachedData.dailyNewUsers);
    const cachedMatchesObj = getCachedAsObject(cachedData.dailyMatchesArray, cachedData.dailyMatches);
    const cachedTurnsObj = getCachedAsObject(cachedData.dailyTurnsArray, cachedData.dailyTurns);
    const cachedSecondsObj = getCachedAsObject(cachedData.dailySecondsArray, cachedData.dailySeconds);
    
    const { sanitized: cachedLogins, staleKeys: staleLoginKeys } = pruneDataset(cachedLoginsObj);
    const { sanitized: cachedNewUsers, staleKeys: staleNewUserKeys } = pruneDataset(cachedNewUsersObj);
    const { sanitized: cachedMatches, staleKeys: staleMatchKeys } = pruneDataset(cachedMatchesObj);
    const { sanitized: cachedTurns, staleKeys: staleTurnKeys } = pruneDataset(cachedTurnsObj);
    const { sanitized: cachedSeconds, staleKeys: staleSecondKeys } = pruneDataset(cachedSecondsObj);
    const cachedRetention = cachedData.retentionData || null;
    
    // Find missing dates for each dataset
    const missingLoginDates = labels.filter(label => !cachedLogins[label]);
    const missingNewUserDates = labels.filter(label => !cachedNewUsers[label]);
    const missingMatchDates = labels.filter(label => !cachedMatches[label]);
    const missingTurnsDates = labels.filter(label => !cachedTurns[label]);
    const missingSecondsDates = labels.filter(label => !cachedSeconds[label]);
    
    // Check if retention data needs to be recalculated (if it's older than 1 day or missing)
    const needsRetentionUpdate = !cachedRetention || 
        (cachedData.lastRetentionUpdate && 
         new Date().getTime() - cachedData.lastRetentionUpdate.toDate().getTime() > 24 * 60 * 60 * 1000);
    
    let countDailyLogins = { ...cachedLogins };
    let countDailyNewUsers = { ...cachedNewUsers };
    let countDailyMatchHistory = { ...cachedMatches };
    let dailyTurnsPlayed = { ...cachedTurns };
    let dailySecondsPlayed = { ...cachedSeconds };
    let retentionData = cachedRetention;
    
    // Fetch missing login data
    if (missingLoginDates.length > 0) {
        console.log("Fetching missing login data for dates:", missingLoginDates);
        const loginQuery = query(
            collectionGroup(Config.getLiveDB(), "logins"),
            where("loginTime", ">=", startDate),
            where("loginTime", "<=", endDate),
            orderBy("loginTime")
        );
        const loginsSnapshot = await getDocs(loginQuery);
        loginsSnapshot.forEach((doc) => {
            const key = new Date(doc.data().loginTime.seconds * 1000).toDateString();
            if (missingLoginDates.includes(key)) {
                countDailyLogins[key] = 1 + (countDailyLogins[key] || 0);
            }
        });
    }
    
    // Fetch missing new user data
    if (missingNewUserDates.length > 0) {
        console.log("Fetching missing new user data for dates:", missingNewUserDates);
        const newUsersQuery = query(
            collection(Config.getLiveDB(), "users"),
            where("creationTime", ">=", startDate),
            where("creationTime", "<=", endDate),
            orderBy("creationTime")
        );
        const newUsersSnapshot = await getDocs(newUsersQuery);
        newUsersSnapshot.forEach((doc) => {
            const key = new Date(doc.data().creationTime.seconds * 1000).toDateString();
            if (missingNewUserDates.includes(key)) {
                countDailyNewUsers[key] = 1 + (countDailyNewUsers[key] || 0);
            }
        });
    }
    
    // Fetch missing match data (includes turns and seconds)
    if (missingMatchDates.length > 0 || missingTurnsDates.length > 0 || missingSecondsDates.length > 0) {
        console.log("Fetching missing match data for dates:", missingMatchDates);
        const matchHistoryQuery = query(
            collectionGroup(Config.getLiveDB(), "matchHistory"),
            where("startDate", ">=", startDate),
            where("startDate", "<=", endDate),
            orderBy("startDate")
        );
        const matchHistorySnapshot = await getDocs(matchHistoryQuery);
        matchHistorySnapshot.forEach((doc) => {
            const key = new Date(doc.data().startDate.seconds * 1000).toDateString();
            const data = doc.data();
            
            if (missingMatchDates.includes(key)) {
                countDailyMatchHistory[key] = 1 + (countDailyMatchHistory[key] || 0);
            }
            
            if (missingTurnsDates.includes(key)) {
                if (!dailyTurnsPlayed[key]) dailyTurnsPlayed[key] = [];
                dailyTurnsPlayed[key].push(data.TurnsOnEnd);
            }
            
            if (missingSecondsDates.includes(key)) {
                if (!dailySecondsPlayed[key]) dailySecondsPlayed[key] = [];
                dailySecondsPlayed[key].push(Math.round(data.MatchDurationInMinutes * 60));
            }
        });
        
        // Divide by 2 since both players have records
        Object.keys(countDailyMatchHistory).forEach((item) => {
            if (typeof countDailyMatchHistory[item] == "number") {
                countDailyMatchHistory[item] = Math.round(countDailyMatchHistory[item] / 2);
            }
        });
    }
    
    // Calculate retention data if needed
    if (needsRetentionUpdate) {
        console.log("Calculating retention data...");
        const retentionEndDate = new Date();
        retentionEndDate.setDate(retentionEndDate.getDate() - 1); // yesterday
        retentionEndDate.setHours(23,59,59,999);
        const retentionStartDate = new Date(retentionEndDate);
        retentionStartDate.setDate(retentionEndDate.getDate() - 30); // 30 days before
        retentionStartDate.setHours(0,0,0,0);
        
        retentionData = await calculateRetention(retentionStartDate, retentionEndDate);
    }
    
    // Update cache with new data
    if (missingLoginDates.length > 0 || missingNewUserDates.length > 0 || missingMatchDates.length > 0 || missingTurnsDates.length > 0 || missingSecondsDates.length > 0 || needsRetentionUpdate || staleLoginKeys.length > 0 || staleNewUserKeys.length > 0 || staleMatchKeys.length > 0 || staleTurnKeys.length > 0 || staleSecondKeys.length > 0) {
        console.log("Updating cache with new data...");
        
        // Convert objects to arrays to avoid Firestore index limits
        const updateData: any = {
            dailyLoginsArray: objectToArray(countDailyLogins),
            dailyNewUsersArray: objectToArray(countDailyNewUsers),
            dailyMatchesArray: objectToArray(countDailyMatchHistory),
            dailyTurnsArray: objectToArray(dailyTurnsPlayed),
            dailySecondsArray: objectToArray(dailySecondsPlayed),
            lastUpdated: new Date()
        };
        
        // Remove old object format fields to prevent index issues
        updateData.dailyLogins = deleteField();
        updateData.dailyNewUsers = deleteField();
        updateData.dailyMatches = deleteField();
        updateData.dailyTurns = deleteField();
        updateData.dailySeconds = deleteField();
        
        if (needsRetentionUpdate) {
            updateData.retentionData = retentionData;
            updateData.lastRetentionUpdate = new Date();
        }
        
        await setDoc(doc(Config.getLiveDB(), "settings", "cachedAnalytics"), updateData, { merge: true });
    }
    
    return { countDailyLogins, countDailyNewUsers, countDailyMatchHistory, dailyTurnsPlayed, dailySecondsPlayed, retentionData };
};

const getExtendedDevMetrics = async (setMetrics:any, fromDate:any, toDate:any, setExtDevMetricsVisibility:any) => {
    ChartJS.register(LineElement, PointElement, LinearScale, Title);


    // Build ordered labels for each day in range (inclusive)
    const startDate = new Date(fromDate);
    startDate.setHours(0,0,0,0);
    const endDate = new Date(toDate);
    endDate.setHours(23,59,59,999);
    const labels:string[] = [];
    {
        const cursor = new Date(startDate);
        while (cursor.getTime() <= endDate.getTime()) {
            labels.push(cursor.toDateString());
            cursor.setDate(cursor.getDate() + 1);
        }
    }

    // Get cached analytics data and fetch missing dates
    const { countDailyLogins, countDailyNewUsers, countDailyMatchHistory, dailyTurnsPlayed, dailySecondsPlayed, retentionData } = await getCachedAnalyticsData(startDate, endDate, labels);




    // Convert cached arrays to flat arrays for calculations
    const allTurnsPlayed: any = [];
    const allSecondsPlayed: any = [];
    
    // Flatten the cached data for average calculations
    Object.values(dailyTurnsPlayed).forEach((turnsArray: any) => {
        if (Array.isArray(turnsArray)) {
            allTurnsPlayed.push(...turnsArray);
        }
    });
    
    Object.values(dailySecondsPlayed).forEach((secondsArray: any) => {
        if (Array.isArray(secondsArray)) {
            allSecondsPlayed.push(...secondsArray);
        }
    });
 
    
    const newUsersSeries = labels.map((label) => countDailyNewUsers[label] || 0);
    const datasets:any[] = [
        {data: newUsersSeries, label:"nr. of new users", borderColor: 'rgb(255, 99, 132)', backgroundColor: 'rgba(255, 99, 132, 0.2)'}
    ];
    if (SHOWMATCHESINGRAPH) {
        const matchSeries = labels.map((label) => countDailyMatchHistory[label] || 0);
        datasets.push({ 
            data: matchSeries, 
            label: "nr. of games played", 
            borderColor: 'rgb(255, 99, 132)', 
            backgroundColor: 'rgba(255, 99, 132, 0.2)' 
        });
    }
    

    const calcAverage = function (array:any) { 
        let sum = 0; 
        for (let i = 0; i < array.length; i++) {
            if (array[i] >= 0) sum += array[i]; 
        }
         
        const average = sum / array.length;  
         
        return average; 
    }; 

    console.log("daily logins (cached)", countDailyLogins);
    console.log("daily new users (cached)", countDailyNewUsers);
    console.log("daily matches played (cached)", countDailyMatchHistory);
    console.log("daily turns (cached)", dailyTurnsPlayed);
    console.log("daily seconds (cached)", dailySecondsPlayed);
    console.log("retention data (cached)", retentionData);
    console.log ("turns played",Math.round(calcAverage(allTurnsPlayed)));
    console.log ("seconds played", calcAverage(allSecondsPlayed)/60);
    console.log ("datasets",  datasets );
    
    let graphData = {labels,datasets};
    let graphOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top' as const,
            },  
            tooltip: {
                callbacks: {
                //   footer: (items:any) => {
                //     return Math.round(items[0].raw*100/totalPlayers) + " % of total players. \n" + Math.round(items[0].raw*100/seasonPlayers) + " % of logged in players.";                    
                //   },
                }
              }   
        },
    };  

    
    let data:any = {
        dailyLogins: countDailyLogins,
        graphData: graphData,
        graphOptions: graphOptions,
        retentionData: retentionData
    };
    if (allTurnsPlayed.length > 0 && allSecondsPlayed.length > 0) {
        data.averageTurns = Math.round(calcAverage(allTurnsPlayed));
        data.averageMinutes = Math.round(calcAverage(allSecondsPlayed)/60);
    }

    setExtDevMetricsVisibility(true);
    setMetrics(data);
}


// getUsers removed (no longer needed)
